<template>
  <div class="slider" v-intersect="intersectConfig">

    <!-- Arrow left -->
    <div class="slider-arrow left"
         :class="{ 'isActive': !isBeginActive, 'isExist': !isBeginExist }"
         v-if="isArrowNeeded"
    >
      <div class="slider-arrow-shadow"></div>
      <button class="slider-arrow-btn" @click="slideToPrevProduct"></button>
    </div>
    <!---->

    <swiper class="swiper slider-block"
            ref="mySwiper"
            :options="swiperOption"
            @slideChange="setMuted"
            v-if="products.length > 0"
    >

      <!-- Swiper -->
      <template v-if="!currentRouteSku">
        <swiper-slide class="slider-block-slide"
                      v-for="(product, index) in products"
                      :key="product.entity_id"
        >
          <product-tile :product="product"
                        :labels-active="false"
                        :only-image="false"
                        :idx="index"
                        :slides-per-view="currentBreakpoint"
          />
        </swiper-slide>
      </template>
      <template v-else>
        <swiper-slide class="slider-block-slide"
                      v-for="(product, index) in products"
                      :key="product.entity_id"
        >
          <product-tile :product="product"
                        :labels-active="false"
                        :only-image="false"
                        :idx="index"
                        :slides-per-view="currentBreakpoint"
          />
        </swiper-slide>
      </template>
      <!---->

    </swiper>
    <div v-else>
      <preloader />
    </div>

    <!-- Arrow right -->
    <div class="slider-arrow right"
         :class="{ 'isActive': !isEndActive, 'isExist': !isEndExist }"
         v-if="isArrowNeeded"
    >
      <div class="slider-arrow-shadow"></div>
      <button class="slider-arrow-btn" @click="slideToNextProduct"></button>
    </div>
    <!---->

  </div>
</template>

<script>
import { formatCategoryLink } from '@vue-storefront/core/modules/url/helpers'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import { mapActions, mapGetters } from 'vuex'
import ProductTile from 'theme/components/core/blocks/Shop/Product/ProductTile'
import Preloader from 'theme/components/theme/blocks/Home/Preloader'
import 'swiper/css/swiper.css'
import { isServer } from '@vue-storefront/core/helpers'
import { curatedProducts } from '@vue-storefront/core/lib/catalogSearch'
import intersect from 'theme/directives/intersect'

export default {
  name: 'ProductsSwiper',
  directives: {
    intersect
  },
  components: {
    ProductTile,
    Preloader,
    Swiper,
    SwiperSlide
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    total: {
      type: Number
    },
    category: {
      type: Object
    }
  },
  data () {
    return {
      // Slider options
      swiperOption: {
        slidesPerView: 4,
        spaceBetween: 0,
        slidesPerGroup: 2,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },
        simulateTouch: false,
        observer: true,
        observeSlideChildren: true,
        breakpoints: {
          1200: {
            slidesPerView: 4
          },
          991: {
            slidesPerView: 3
          },
          768: {
            slidesPerView: 3
          },
          465: {
            slidesPerView: 3
          },
          0: {
            slidesPerView: 2
          }
        }
      },
      isEnd: false,
      isEndActive: false,
      isEndExist: false,
      isBegin: true,
      isBeginActive: true,
      isBeginExist: true,
      size: 12,
      from: 12,
      products: []
    }
  },
  created () {
    this.products = this.allProducts.filter(product => product.category_ids.includes(this.category.entity_id))
  },
  beforeDestroy () {
    const swiper = this.$refs.mySwiper && this.$refs.mySwiper.$swiper
    if (swiper && swiper.initialized) {
      swiper.destroy(true, true)
    }
  },
  watch: {
    isBegin () {
      this.animateBeginArrow(this.isBegin)
    },
    isEnd () {
      this.animateEndArrow(this.isEnd)
    }
  },
  computed: {
    ...mapGetters({
      allProducts: 'product/list'
    }),
    currentPartnerCode () {
      return this.$route.params.store
    },
    currentLocationCode () {
      return this.$route.params.locationCode
    },
    currentRouteSku () {
      return this.$route.params.childSku
    },
    isArrowNeeded () {
      if (isServer) {
        return false
      }
      if (window.innerWidth > 1200) {
        return this.products.length > 4
      } else if (window.innerWidth > 991 && window.innerWidth < 1200) {
        return this.products.length > 3
      } else if (window.innerWidth > 768 && window.innerWidth < 991) {
        return this.products.length > 4
      } else if (window.innerWidth > 465 && window.innerWidth < 768) {
        return this.products.length > 3
      }
    },
    currentBreakpoint () {
      if (isServer) {
        return 1
      }
      if (window.innerWidth > 1200) {
        return 4
      } else if (window.innerWidth > 991 && window.innerWidth < 1200) {
        return 3
      } else if (window.innerWidth > 768 && window.innerWidth < 991) {
        return 4
      } else if (window.innerWidth > 465 && window.innerWidth < 768) {
        return 3
      } else {
        return 1
      }
    },
    intersectConfig () {
      if (this.products.length) {
        return false
      }
      return {
        margin: '50px',
        threshold: 0.1,
        onIntersect: this.updateProducts
      }
    }
  },
  methods: {
    ...mapActions({
      loadProducts: 'product/list'
    }),

    async updateProducts (size = 12, start = 0) {
      let products = await this.loadProducts({
        semanticFetch: () => curatedProducts({ categoryIds: [this.category.entity_id], start, size })
      })
      this.products.push(...products.items)
      await this.$nextTick()
      if (this.$refs.mySwiper) {
        this.$refs.mySwiper.$swiper.update()
      }
    },
    loadMoreProducts () {
      let start = this.from > this.products.length ? this.from : this.products.length
      this.updateProducts(this.size, start)
    },
    setMuted () {
      let currentIdx = this.$refs.mySwiper.$swiper.activeIndex
      if (currentIdx >= this.products.length - 6 && this.products.length < this.total) {
        this.loadMoreProducts()
      }
    },
    categoryLink (category) {
      return `/${this.currentLocationCode}/${this.currentPartnerCode}${formatCategoryLink(category)}`
    },

    // Slide to previous product
    slideToPrevProduct () {
      this.$refs.mySwiper.$swiper.slidePrev()
      this.sliderPosition()
    },

    // Slide to next product
    slideToNextProduct () {
      this.$refs.mySwiper.$swiper.slideNext()
      this.sliderPosition()
    },

    // Check slider position
    sliderPosition () {
      if (this.$refs.mySwiper.$swiper.isBeginning) this.isBegin = true
      else this.isBegin = false

      if (this.$refs.mySwiper.$swiper.isEnd) this.isEnd = true
      else this.isEnd = false
    },

    // Animate left arrow
    animateBeginArrow (status) {
      if (status) {
        this.isBeginExist = true
        this.isBeginActive = true
      } else {
        this.isBeginActive = false
        setTimeout(() => { this.isBeginExist = false }, 100)
      }
    },

    // Animate right arrow
    animateEndArrow (status) {
      if (status) {
        this.isEndExist = true
        this.isEndActive = true
      } else {
        this.isEndActive = false
        setTimeout(() => { this.isEndExist = false }, 100)
      }
    }
  }
}
</script>

<style lang="scss">
  .slider {

    &-block {
      margin-bottom: 20px;
      position: relative;
      z-index: 0;

      &-slide {
        height: auto;
      }
    }

    &-arrow {
      width: 18px;
      height: 100%;
      margin: 0;

      display: none;

      position: absolute;
      top: 0;
      bottom: 0;
      z-index: 1;

      &.isExist {
        display: block;

        @media only screen and (max-width: 465px) {
          display: none;
        }
      }

      &.isActive {
        &.left {
          & .slider-arrow-shadow {
            background: linear-gradient(to left, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 80%);
          }
        }

        &.right {
          & .slider-arrow-shadow {
            background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 80%);
          }
        }
      }

      &.left {
        left: 0;

        & .slider-arrow-shadow {
          left: 0;
        }

        & .slider-arrow-btn {
          left: 0;
          background-image: url('../../../../../assets/shop/arrow-left.png');
        }
      }

      &.right {
        right: 0;

        & .slider-arrow-shadow {
          right: 0;
        }

        & .slider-arrow-btn {
          right: 0;
          background-image: url('../../../../../assets/shop/arrow-right.png');
        }
      }

      &-shadow {
        width: 60px;
        height: 100%;
        margin: 0;

        position: absolute;
        top: 0;
        bottom: 0;

        background: transparent;
        transition: .2s background;
      }

      &-btn {
        width: 18px;
        height: 100%;
        margin: 0;

        position: absolute;
        top: 0;
        bottom: 0;

        background-color: transparent;
        border: none;

        background-position: center;
        background-size: 18px 20px;
        background-repeat: no-repeat;
        outline: none;
      }
    }
  }

  .swiper-button-disabled {
    display: none !important;
  }
</style>
